import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { useToast } from '../components/ui/Toast';
import SafeIcon from '../components/common/SafeIcon';
import useWorkspace from '../hooks/useWorkspace';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiClock, FiEdit, FiCheck, FiRotateCcw, FiPlay, FiCalendar, FiInbox, FiX } = FiIcons;

const localDate = (date = new Date()) => {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};

export default function DailyPlanner() {
  const { tasks, addTask, updateTask, storageError } = useWorkspace();
  const [activeTab, setActiveTab] = useState('today');
  const [selectedDate, setSelectedDate] = useState(localDate());
  const [editingTask, setEditingTask] = useState(null);
  const { success } = useToast();
  const navigate = useNavigate();

  const visibleTasks = useMemo(() => tasks
    .filter((item) =>
      activeTab === 'all'
        || item.scheduledDate === selectedDate
        || (!item.scheduledDate && item.status !== 'completed')
    )
    .sort((a, b) => {
      if (a.status === 'completed' && b.status !== 'completed') return 1;
      if (a.status !== 'completed' && b.status === 'completed') return -1;
      return (a.scheduledTime || '99:99').localeCompare(b.scheduledTime || '99:99');
    }), [tasks, activeTab, selectedDate]);

  const inboxTasks = visibleTasks.filter((item) => !item.scheduledDate);
  const scheduledTasks = visibleTasks.filter((item) => item.scheduledDate);

  const handleSaveTask = (taskData) => {
    if (editingTask?.id) {
      updateTask(editingTask.id, taskData);
      success('Task updated!');
    } else {
      addTask(taskData);
      success('Task added to your day!');
    }
    setEditingTask(null);
  };

  const handleToggleTask = (item) => {
    updateTask(item.id, {
      status: item.status === 'completed' ? 'todo' : 'completed',
    });
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Hero Header */}
      <div className="relative rounded-3xl overflow-hidden shadow-xl bg-pine text-paper p-6 lg:p-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-paper/60 mb-2">Plan</p>
            <h1 className="text-3xl lg:text-4xl font-cormorant font-bold text-paper">
              Make room for what matters.
            </h1>
            <p className="text-paper/70 font-dm-sans max-w-lg mt-2">
              A clear, connected plan for focused work.
            </p>
          </div>
          <Button
            variant="paper"
            onClick={() => setEditingTask({ scheduledDate: selectedDate })}
            icon={FiPlus}
          >
            Add task
          </Button>
        </div>
      </div>

      {storageError && (
        <p role="alert" className="rounded-xl bg-crimson/10 px-4 py-3 text-sm text-crimson">
          {storageError}
        </p>
      )}

      <Card className="p-4">
        <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3">
          <div className="flex rounded-2xl bg-pine/5 p-1" role="tablist" aria-label="Task views">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'today'}
              onClick={() => setActiveTab('today')}
              className={`flex-1 px-5 py-2 rounded-xl text-sm font-bold ${
                activeTab === 'today' ? 'bg-pine text-paper shadow-sm' : 'text-pine/60 hover:text-pine'
              }`}
            >
              Today
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'all'}
              onClick={() => setActiveTab('all')}
              className={`flex-1 px-5 py-2 rounded-xl text-sm font-bold ${
                activeTab === 'all' ? 'bg-pine text-paper shadow-sm' : 'text-pine/60 hover:text-pine'
              }`}
            >
              All tasks
            </button>
          </div>

          <label className="flex items-center gap-2 text-sm font-bold text-pine">
            <SafeIcon icon={FiCalendar} />
            <span className="sr-only">Plan date</span>
            <input
              type="date"
              value={selectedDate}
              onChange={(event) => setSelectedDate(event.target.value)}
              className="flex-1 rounded-xl border border-pine/15 bg-paper px-3 py-2 text-pine focus:outline-none focus:ring-2 focus:ring-sage"
            />
          </label>
        </div>
      </Card>

      <TaskList
        title="Unscheduled inbox"
        icon={FiInbox}
        tasks={inboxTasks}
        emptyText="No unscheduled tasks."
        onEdit={setEditingTask}
        onToggle={handleToggleTask}
        onFocus={(item) => navigate(`/studio?task=${encodeURIComponent(item.id)}`)}
      />

      <TaskList
        title={activeTab === 'today' ? 'Scheduled' : 'Tasks'}
        icon={FiClock}
        tasks={scheduledTasks}
        emptyText={activeTab === 'today' ? 'Nothing scheduled for this date.' : 'No scheduled tasks yet.'}
        onEdit={setEditingTask}
        onToggle={handleToggleTask}
        onFocus={(item) => navigate(`/studio?task=${encodeURIComponent(item.id)}`)}
      />

      {visibleTasks.length === 0 && (
        <Card className="p-8 text-center border-dashed bg-sage/5">
          <p className="text-pine/60">
            Your plan is clear. Add only what deserves your attention.
          </p>
          <Button
            className="mt-4"
            size="sm"
            icon={FiPlus}
            onClick={() => setEditingTask({ scheduledDate: selectedDate })}
          >
            Add your first task
          </Button>
        </Card>
      )}

      <TaskDialog
        task={editingTask}
        selectedDate={selectedDate}
        onClose={() => setEditingTask(null)}
        onSave={handleSaveTask}
      />
    </div>
  );
}

function TaskList({ title, icon, tasks, emptyText, onEdit, onToggle, onFocus }) {
  return (
    <Card className="p-4 lg:p-6 bg-white/90 backdrop-blur-lg">
      <h2 className="text-xl font-cormorant font-bold text-charcoal mb-4 flex items-center gap-2">
        <SafeIcon icon={icon} className="w-5 h-5 text-sage" />
        {title}
      </h2>

      {tasks.length > 0 ? (
        <div className="divide-y divide-pine/10">
          {tasks.map((item) => (
            <div key={item.id} className="py-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <button
                type="button"
                onClick={() => onToggle(item)}
                className={`w-10 h-10 flex-shrink-0 rounded-xl border flex items-center justify-center ${
                  item.status === 'completed'
                    ? 'bg-sage text-pine border-sage'
                    : 'border-pine/15 text-pine hover:bg-sage/15'
                }`}
                aria-label={`${item.status === 'completed' ? 'Undo' : 'Complete'} ${item.title}`}
              >
                <SafeIcon icon={item.status === 'completed' ? FiRotateCcw : FiCheck} />
              </button>

              <div className="min-w-0 flex-1">
                <p className={`font-dm-sans font-bold text-charcoal ${
                  item.status === 'completed' ? 'line-through opacity-50' : ''
                }`}>
                  {item.title}
                </p>
                <p className="text-xs text-charcoal/50 mt-1">
                  {[item.scheduledDate, item.scheduledTime, item.project, `${item.estimatedMinutes || 25} min`]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>

              <div className="flex gap-2">
                <Button variant="ghost" size="sm" icon={FiEdit} onClick={() => onEdit(item)}>
                  Edit
                </Button>
                {item.status !== 'completed' && (
                  <Button size="sm" icon={FiPlay} onClick={() => onFocus(item)}>
                    Focus
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="rounded-2xl bg-pine/5 p-5 text-sm text-pine/55">
          {emptyText}
        </p>
      )}
    </Card>
  );
}

function TaskDialog({ task, selectedDate, onClose, onSave }) {
  const dialogRef = useRef(null);
  const titleRef = useRef(null);
  const projectRef = useRef(null);
  const dateRef = useRef(null);
  const timeRef = useRef(null);
  const minutesRef = useRef(null);

  useEffect(() => {
    if (!task) return undefined;

    const previousFocus = document.activeElement;
    const focusTimer = setTimeout(() => titleRef.current?.focus(), 0);

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !dialogRef.current) return;
      const controls = [...dialogRef.current.querySelectorAll('button, input')]
        .filter((element) => !element.disabled);

      if (!controls.length) return;
      const first = controls[0];
      const last = controls[controls.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleKeyDown);
      previousFocus?.focus?.();
    };
  }, [task, onClose]);

  if (!task) return null;

  const handleSubmit = (event) => {
    event.preventDefault();
    const title = titleRef.current?.value.trim();

    if (!title) {
      titleRef.current?.focus();
      return;
    }

    onSave({
      title,
      status: task.status || 'todo',
      project: projectRef.current?.value.trim() || 'General',
      scheduledDate: dateRef.current?.value || '',
      scheduledTime: timeRef.current?.value || '',
      estimatedMinutes: Math.max(5, Number(minutesRef.current?.value) || 25),
    });
  };

  const inputClasses = 'w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sage/50';

  return (
    <div
      className="fixed inset-0 z-50 bg-pine/55 backdrop-blur-sm p-4 flex items-end sm:items-center justify-center"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="task-dialog-title"
        className="w-full max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl"
      >
        <div className="flex items-center justify-between p-5 border-b border-sage/20">
          <h2 id="task-dialog-title" className="text-xl font-cormorant font-bold text-charcoal">
            {task.id ? 'Edit Task' : 'Add Task'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-charcoal/60 hover:text-charcoal hover:bg-sage/10 rounded-xl"
            aria-label="Close task editor"
          >
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-4">
            <label className="block text-sm font-bold text-charcoal">
              Title
              <input
                ref={titleRef}
                defaultValue={task.title || ''}
                className={`${inputClasses} mt-1`}
                placeholder="Focus task..."
                required
              />
            </label>

            <label className="block text-sm font-bold text-charcoal">
              Project (Optional)
              <input
                ref={projectRef}
                defaultValue={task.project === 'General' ? '' : task.project || ''}
                className={`${inputClasses} mt-1`}
                placeholder="Project"
              />
            </label>

            <div className="grid grid-cols-2 gap-4">
              <label className="block text-sm font-bold text-charcoal">
                Date (Optional)
                <input
                  ref={dateRef}
                  type="date"
                  defaultValue={task.scheduledDate ?? selectedDate}
                  className={`${inputClasses} mt-1`}
                />
              </label>

              <label className="block text-sm font-bold text-charcoal">
                Time (Optional)
                <input
                  ref={timeRef}
                  type="time"
                  defaultValue={task.scheduledTime || ''}
                  className={`${inputClasses} mt-1`}
                />
              </label>
            </div>

            <label className="block text-sm font-bold text-charcoal">
              Estimated Minutes
              <input
                ref={minutesRef}
                type="number"
                min="5"
                step="5"
                defaultValue={task.estimatedMinutes || 25}
                className={`${inputClasses} mt-1`}
              />
            </label>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" variant="primary">
                {task.id ? 'Save Task' : 'Add Task'}
              </Button>
            </div>
          </div>
        </form>
      </section>
    </div>
  );
}